import { Sport } from 'types/Sport';
import styles from './LiveSportEvents.module.css';
import { LiveEvent } from '../LiveEvent/LiveEvent';
import React, { useState } from 'react';
import { SportIDs } from 'types/enums';
import { LiveEvent as LiveEventDto } from 'types/LiveEvent';
import { ThemeIcon } from 'components/ThemeIcon/ThemeIcon';
import type { ThemeIconKey } from 'lib/themeIcons';
import { SportsHeader } from './SportsHeader/SportsHeader';
import { useTranslation } from 'react-i18next';
import { useProgressiveCount } from 'hooks/useProgressiveCount';
import { getMatchDateLabel } from 'helpers/dateHelper';
import classNames from 'classnames';
import { useModernDesktop } from 'components/Layout/ModernDesktopShell';
import {
  LiveMarketKey,
  liveMarketsFor,
  liveMarketsOn,
} from 'components/mobile/LiveBetting/liveMarkets';
import {
  MarketPicker,
  tabLabel,
} from 'components/mobile/LiveBetting/LiveBetting';
import { LIVE_FULL_TIME_BET_MAP } from 'config/constants';
import chips from 'components/mobile/LiveBetting/LiveBetting.module.css';

interface Props {
  sport: Sport;
  events: LiveEventDto[];
  favorite?: boolean;
  /** Passed through to `SportsHeader` (e.g. upcoming tab title). */
  titleOverride?: string;
  labelsOverride?: string[];
  iconOverrideKey?: ThemeIconKey;
  hideChevron?: boolean;
  /** When false, skip the 1/X/2 + O/U sub-header row (live layout still uses the outer `.wrapper`). */
  showSubHeader?: boolean;
  /** Upcoming prematch rows: show sport logo between teams instead of 0:0. */
  prematchUpcoming?: boolean;
}

export const LiveSportEvents = ({
  sport,
  events,
  favorite = false,
  titleOverride,
  labelsOverride,
  iconOverrideKey,
  hideChevron = false,
  showSubHeader = true,
  prematchUpcoming = false,
}: Props) => {
  const [showEvents, setShowEvents] = useState(true);
  const [marketKey, setMarketKey] = useState<LiveMarketKey>('full');
  const drawn = useProgressiveCount(events.length);
  const { t } = useTranslation();
  // Modern has no market columns: chips pick which odds the rows show (1 X 2,
  // double chance, next goal...), as on the phone. A chip only for a market
  // some match on the list has just now.
  const modernDesktop = useModernDesktop();
  const markets = modernDesktop
    ? liveMarketsFor(sport.id).filter(
        (m) =>
          !m.pick || events.some((ev) => !!ev && !!m.pick!(ev)?.odds?.length)
      )
    : [];
  // Classic: the first column's market is picked from a small list over it,
  // as on the phone (owner, 2026-11-02); the other columns stay.
  const columnMarkets =
    !modernDesktop && sport.id && !prematchUpcoming
      ? liveMarketsOn(
          sport.id,
          events,
          LIVE_FULL_TIME_BET_MAP.get(sport.id)
        ).filter(
          // Only what fits the column's three cells.
          (m) =>
            m.key === 'full' ||
            events.some((ev) => {
              const n = (ev && m.pick?.(ev)?.odds?.length) || 0;
              return n > 0 && n <= (m.totals ? 2 : 3);
            })
        )
      : [];
  const market = modernDesktop
    ? markets.find((m) => m.key === marketKey) ?? markets[0]
    : columnMarkets.find((m) => m.key === marketKey);
  const columnMarket = market ?? columnMarkets[0];

  // Upcoming rows run past midnight: a "Danas" / "Sutra" row opens each day.
  const dayOf = (ev: LiveEventDto) =>
    prematchUpcoming && ev.kickoffUnix
      ? t(getMatchDateLabel(new Date(ev.kickoffUnix * 1000)))
      : '';

  const getSubHeader = (sportId: number) => {
    const overUnder = (
      <div>
        <span></span>
        <span>+</span>
        <span>-</span>
      </div>
    );

    const tip1X2Header = (
      <div>
        <span>1</span>
        <span>X</span>
        <span>2</span>
      </div>
    );

    const tip12Header = (
      <div>
        <span>1</span>
        <span></span>
        <span>2</span>
      </div>
    );

    // Another market picked for the first column: its picks are not 1 X 2.
    const picked = !modernDesktop && market && market.key !== 'full';
    const first = (header: JSX.Element) =>
      picked ? (
        <div>
          <span></span>
          <span></span>
          <span></span>
        </div>
      ) : (
        header
      );

    switch (sportId) {
      case SportIDs.SOCCER:
        return (
          <div className={styles.subHeader}>
            {first(tip1X2Header)}
            {tip1X2Header}
            {tip1X2Header}
            {overUnder}
          </div>
        );
      case SportIDs.TENNIS:
        return (
          <div className={styles.subHeader}>
            {first(tip12Header)}
            {tip12Header}
            <div></div>
            {overUnder}
          </div>
        );
      case SportIDs.BASKETBALL:
        return (
          <div className={styles.subHeader}>
            {first(tip1X2Header)}
            <div></div>
            <div>
              <span></span>
              <span>1</span>
              <span>2</span>
            </div>
            {overUnder}
          </div>
        );
    }
  };

  const headerSport: Sport = {
    ...sport,
    count: typeof sport.count === 'number' ? sport.count : events.length,
  };

  return (
    <div className={styles.wrapper}>
      {!sport.id && (
        <div className={styles.header}>
          <div className={styles.sportInfo}>
            <ThemeIcon
              icon="star-white"
              alt={sport.name}
              variant="inline"
              tone="chrome"
            />
            <span>Favorites</span>
            {<span className={styles.count}>{events.length}</span>}
          </div>
        </div>
      )}
      <SportsHeader
        sport={headerSport}
        titleOverride={titleOverride}
        labelsOverride={labelsOverride}
        iconOverrideKey={iconOverrideKey}
        hideChevron={hideChevron}
        firstColumn={
          columnMarkets.length > 1 && columnMarket ? (
            <MarketPicker
              markets={columnMarkets}
              current={columnMarket}
              label={(m) => tabLabel(m, sport.id, t)}
              onPick={(m) => setMarketKey(m.key)}
            />
          ) : undefined
        }
        onExpandClick={
          events.length > 0 && !hideChevron
            ? () => setShowEvents(!showEvents)
            : () => {}
        }
      />
      {showEvents && events && events.length > 0 && (
        <>
          {showSubHeader && getSubHeader(sport.id)}
          {markets.length > 1 && (
            <div className={chips.marketChips}>
              {markets.map((m) => (
                <button
                  type="button"
                  key={m.key}
                  className={classNames(
                    chips.marketChip,
                    m.key === market?.key && chips.marketChipOn
                  )}
                  onClick={() => setMarketKey(m.key)}
                >
                  {t(m.label)}
                </button>
              ))}
            </div>
          )}
          {events.slice(0, drawn).map((x, index) => {
            if (!x) {
              return <React.Fragment key={index} />;
            }
            const day = dayOf(x);
            const newDay =
              day !== '' && (index === 0 || day !== dayOf(events[index - 1]));
            return (
              <React.Fragment key={x.fi}>
                {newDay && <div className={styles.dayRow}>› {day}</div>}
                <LiveEvent
                  event={x}
                  isFavorite={favorite}
                  oddRow={!!(index % 2)}
                  prematchUpcoming={prematchUpcoming}
                  market={market}
                />
              </React.Fragment>
            );
          })}
        </>
      )}
    </div>
  );
};
